import {
	Autocomplete,
	Checkbox,
	createFilterOptions,
	TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
	getCategoriesStatus,
	getItemCategories,
	selectGetCategoriesById,
} from "../../../redux/ProductsSlice";
import React, { useContext, useEffect, useState } from "react";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { produce } from "immer";
import { IsOnboardedContext } from "../../../Contexts/IsOnboardedContext";

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const CategoriesAutocomplete = ({
	initialValue,
	width,
	setActiveForm,
	categoryOptions,
	formValid,
	createNewOptionsAllowed,
}) => {
	const dispatch = useDispatch();
	const filter = createFilterOptions();
	const categories = !categoryOptions
		? useSelector(selectGetCategoriesById)
		: categoryOptions;

	const categoriesStatus = useSelector(getCategoriesStatus);
	const context = useContext(IsOnboardedContext);

	const [value, setValue] = useState(initialValue ?? []);

	const handleChange = (event, newValue, reason) => {
		if (reason === "removeOption" || reason === "clear") {
			setActiveForm((prevState) =>
				produce(prevState, (draft) => {
					draft.categories = draft.categories.filter((category) =>
						newValue.some((newCategory) => newCategory.name === category.name)
					);
				})
			);
			setValue(newValue);

			if (formValid) {
				formValid.current.categories = {};
				newValue.forEach((value) => {
					formValid.current.categories[value.name] = {};
				});
			}
		}

		updateCategories(newValue);
	};

	const updateCategories = (newValue) => {
		const uniqueValues = newValue.reduce((acc, item) => {
			const name = item.inputValue ? item.inputValue : item.name;

			if (!acc.find((i) => i.name === name)) {
				acc.push(
					item.inputValue ? { name: item.inputValue, productItems: [] } : item
				);
			}
			return acc;
		}, []);

		setActiveForm((prevState) =>
			produce(prevState, (draft) => {
				uniqueValues.forEach((value) => {
					const existingCategory = draft.categories.find(
						(cat) => cat.name === value.name
					);
					if (!existingCategory) {
						const categoryData = { ...value, createdBy: context.userName };
						draft.categories.push(categoryData);
					}
				});

				if (formValid) {
					if (!formValid.current.categories) {
						formValid.current.categories = {};
					}

					uniqueValues.forEach((value) => {
						if (!formValid.current.categories[value.name]) {
							formValid.current.categories[value.name] = {};
						}
					});
				}
			})
		);

		setValue((prevValue) => {
			const existingNames = prevValue.map((item) => item.name);
			return [
				...prevValue,
				...uniqueValues.filter((item) => !existingNames.includes(item.name)),
			];
		});
	};

	useEffect(() => {
		if (!categoryOptions && categoriesStatus === "idle") {
			dispatch(getItemCategories());
		}
	}, [dispatch, categoriesStatus]);

	return (
		<>
			<Autocomplete
				multiple
				id='categories'
				options={categories ?? []}
				disableCloseOnSelect
				onChange={handleChange}
				value={value}
				getOptionLabel={(option) => {
					if (typeof option === "string") {
						return option;
					}
					if (option.inputValue) {
						return option.inputValue;
					}
					return option.name;
				}}
				filterOptions={(options, params) => {
					const filtered = filter(options, params);
					const { inputValue } = params;
					const isExisting = options.some(
						(option) => inputValue === option.name
					);

					// Only allow adding new options if createNewOptionsAllowed is true
					if (createNewOptionsAllowed && inputValue !== "" && !isExisting) {
						filtered.push({
							inputValue: inputValue,
							name: `Add "${inputValue}"`,
						});
					}

					return filtered;
				}}
				selectOnFocus
				clearOnBlur
				handleHomeEndKeys
				renderOption={(props, option, { selected }) => {
					const { key, ...optionProps } = props;
					return (
						<li
							key={key}
							{...optionProps}
						>
							<Checkbox
								icon={icon}
								checkedIcon={checkedIcon}
								checked={selected}
							/>
							{option?.name}
						</li>
					);
				}}
				isOptionEqualToValue={(option, value) => option.name === value.name}
				renderInput={(params) => (
					<TextField
						{...params}
						label='Categories'
						placeholder={
							value?.length > 0
								? null
								: createNewOptionsAllowed
								? "Create new or select"
								: "Select"
						}
						sx={{
							width: width ?? {
								xs: "200px !important",
								sm: "250px !important",
								md: "300px !important",
							},
						}}
					/>
				)}
			/>
		</>
	);
};

export default CategoriesAutocomplete;
